import BpkButton, {
  BUTTON_TYPES,
  SIZE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkOverlay, { OVERLAY_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-overlay';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  SponsoredInfoButton,
  getSponsoredCallToActionData,
} from '@web-platform/shared-universal-features-adverts';

import STYLES from './HomepageHero.module.scss';

export type HomepageHeroProps = {
  contentId?: string;
  headline: string;
  subheading: string;
  logo: string;
  altText: string;
  buttonText: string;
  onClick: () => void;
  style?: {};
  fontColor?: string;
  ctaBackgroundColour?: string;
  ctaFontColour?: string;
  pioPartnerName: string;
  pioAgencyName?: string;
};

const constructAriaLabel = ({
  altText,
  buttonText,
  headline,
  subheading,
}: Pick<HomepageHeroProps, 'buttonText' | 'headline' | 'subheading' | 'altText'>) => {
  const text: string[] = [];
  const addText = (value: string) => value && text.push(value);

  addText(headline);
  addText(subheading);
  addText(altText);
  addText(buttonText);

  return text.join('. ');
};

export const HomepageHero = ({
  altText,
  buttonText,
  contentId,
  ctaBackgroundColour,
  ctaFontColour,
  fontColor,
  headline,
  logo,
  onClick,
  pioAgencyName,
  pioPartnerName,
  style,
  subheading,
}: HomepageHeroProps) => {
  const onClickWrapper = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.stopPropagation();
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Spacebar' || e.key === ' ') {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      onClick();
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Spacebar' || e.key === ' ') {
      e.stopPropagation();
      e.preventDefault();
      onClick();
    }
  };

  const callToActionData = getSponsoredCallToActionData({ pioPartnerName, pioAgencyName });

  return (
    <div className={STYLES.parent}>
      <div
        id={contentId}
        className={STYLES.container}
        onClick={onClick}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        role="button"
        style={style}
        aria-label={constructAriaLabel({ altText, buttonText, headline, subheading })}
        data-testid="homepage-hero-container"
      >
        <BpkOverlay
          overlayType={OVERLAY_TYPES.bottomHigh}
          className={STYLES.overlay}
          aria-hidden
          foregroundContent={
            <div className={STYLES.contentContainer}>
              <div className={STYLES.content} aria-hidden>
                <div className={STYLES.containerRow1}>
                  <BpkText
                    tagName="h2"
                    textStyle={TEXT_STYLES.hero5}
                    className={STYLES.containerRow1Headline}
                  >
                    {headline}
                  </BpkText>
                  <BpkButton
                    size={SIZE_TYPES.large}
                    type={BUTTON_TYPES.primaryOnDark}
                    onClick={onClickWrapper}
                    className={STYLES.containerRow1CTA}
                    tabIndex={-1} /* button is not tab-focusable for accessibility */
                    style={{
                      backgroundColor: ctaBackgroundColour || undefined,
                      color: ctaFontColour || undefined,
                    }}
                  >
                    {buttonText}
                  </BpkButton>
                </div>
                <div className={STYLES.containerBreak} />
              </div>
            </div>
          }
        >
          {/* Overlay requires a child component */}
          {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
          <></>
        </BpkOverlay>
      </div>
      <div className={STYLES.brandInfo}>
        <div className={STYLES.containerRow2}>
          <img className={STYLES.containerRow2Logo} src={logo} alt="" aria-hidden />
          <div className={STYLES.sponsoredButton} style={{ color: fontColor, fill: fontColor }}>
            <SponsoredInfoButton infoIconCustomColor callToAction={callToActionData}>
              <BpkText tagName="span" textStyle={TEXT_STYLES.label3}>
                {subheading}
              </BpkText>
            </SponsoredInfoButton>
          </div>
        </div>
      </div>
    </div>
  );
};
